/**
 * One lane-v2 send, minus React: what the composer does with the store, the chip and the paperclips. Kept pure over an
 * injected store so the rules that matter (the chip rides, clears only on "sent", clears only if unchanged, a follow-up
 * leaves it alone, a refused cap sends nothing) are unit-testable in the node suite.
 */
import type { AttachmentIn, IdeStateIn } from "./types";
import { turnInputsFor, type ContextFile } from "./contextFile";
import { canSendFollowUp } from "./followUps";

/** What a send carried. Handed to `onAccepted` for THAT send only: nothing is parked in a shared slot. */
export interface RodeInputs {
  paperclips: boolean;
  /** The chip that rode this turn; to be cleared only if it is still the chip on screen. */
  contextFile: ContextFile | null;
}

export interface SendStore {
  activeTurnId: string | null;
  followUps: Parameters<typeof canSendFollowUp>[0];
  turnAttachments: boolean;
  turnIdeState: boolean;
  sendFollowUp: (text: string, opts?: { withoutFile?: boolean }) => Promise<unknown>;
  sendTurn: (
    text: string,
    opts: {
      attachments?: AttachmentIn[];
      ideState?: IdeStateIn | null;
      onAccepted?: () => void;
      previews?: Readonly<Record<string, string>>;
    },
  ) => Promise<string | null | undefined>;
}

export type SendOutcome =
  | { kind: "follow_up" }
  | { kind: "refused"; refusal: string }
  | { kind: "turn"; started: boolean };

export async function sendWithInputs(args: {
  store: SendStore;
  text: string;
  chip: ContextFile | null;
  paperclips: readonly AttachmentIn[];
  repoIdeState: IdeStateIn | null;
  /** Runs once if the server accepts this send, with what it carried. Not called for a refusal or a failed send. */
  onAccepted?: (rode: RodeInputs) => void;
  /** The composer's picture of each image paperclip, by name; shown on the sent question (never sent). */
  previews?: Readonly<Record<string, string>>;
}): Promise<SendOutcome> {
  const { store, text, chip, paperclips, repoIdeState, onAccepted, previews } = args;
  // A running turn takes the message in its inbox; attachments and the chip do not ride it and stay where they are
  // (pending, and the follow-up row says so: `withoutFile`).
  if (store.activeTurnId && canSendFollowUp(store.followUps, store.activeTurnId)) {
    if (await store.sendFollowUp(text, { withoutFile: !!chip || paperclips.length > 0 })) return { kind: "follow_up" };
  }
  const inputs = turnInputsFor(store, chip, paperclips, repoIdeState);
  if (inputs.refusal) return { kind: "refused", refusal: inputs.refusal };
  const rode: RodeInputs = { paperclips: inputs.rodePaperclips, contextFile: inputs.rodeContext ? chip : null };
  const carried = inputs.attachments.length > 0;
  const localId = await store.sendTurn(text, {
    attachments: carried ? inputs.attachments : undefined,
    ideState: inputs.ideState,
    // The settle is this send's own: it fires from this request's result and is keyed by nothing shared.
    onAccepted: carried && onAccepted ? () => onAccepted(rode) : undefined,
    ...(previews && inputs.rodePaperclips ? { previews } : {}),
  });
  return { kind: "turn", started: !!localId };
}

/**
 * What an accepted send removes from the composer: exactly the attachments it carried (by id, so one added while it was
 * in flight stays), and the chip only if it is still the same one. If the conversation changed since the send (the reset
 * epoch moved) the composer was already cleared for the new one, and a late accept touches nothing: a file or chip the
 * reader picked since is theirs, even if it is the same file.
 */
export function clearAccepted(
  rode: RodeInputs,
  carriedIds: readonly string[],
  epochAtSend: number,
  deps: { epoch: () => number; removeAttachment: (id: string) => void; currentChip: () => ContextFile | null; clearChip: () => void },
): void {
  if (deps.epoch() !== epochAtSend) return;
  if (rode.paperclips) for (const id of carriedIds) deps.removeAttachment(id);
  if (rode.contextFile && deps.currentChip() === rode.contextFile) deps.clearChip();
}
